import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import Loader from '../components/Loader';
import { supabase } from '../config/supabaseClient';
import { getReferralDetail } from '../services/referralService';
import { getReferralCommission } from '../services/settingsService';
import { formatUSD, getTimeAgo } from '../services/walletAnalyticsService';
import '../components/AdminLayout.css';

const PAGE_SIZE = 15;

// Postgres NUMERIC/BIGINT arrive from supabase-js as strings. Coerce before any
// arithmetic or comparison, or sorting silently goes lexicographic.
const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const time = (v) => (v ? new Date(v).getTime() : 0);

const fullDate = (v) =>
    v ? new Date(v).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }) : '';

// Relative for the recent past, an explicit date beyond that.
const smartDate = (v) => {
    if (!v) return '—';
    const days = (Date.now() - new Date(v).getTime()) / 86400000;
    return days < 30 ? getTimeAgo(v) : new Date(v).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
    });
};

// A referral is Credited when a confirmed bonus row exists for it, Pending
// otherwise. Derived rather than read off the row so that a bonus in any other
// state still reads as Pending — which is what an unpaid referral is.
const isCredited = (r) => r.bonus_status === 'confirmed';

const SORT_ACCESSORS = {
    name: (r) => (r.full_name || r.email || '').toLowerCase(),
    signedUp: (r) => time(r.signed_up_at),
    verified: (r) => (r.is_verified ? 1 : 0),
    status: (r) => (isCredited(r) ? 1 : 0),
    bonus: (r) => (isCredited(r) ? num(r.bonus_amount_usd) : -1),
    creditedOn: (r) => time(r.bonus_confirmed_at),
};

const TEXT_COLUMNS = ['name'];

function AdminReferralDetail() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user: currentAdmin } = useAuth();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [referrer, setReferrer] = useState(null);
    const [wallet, setWallet] = useState(null);
    const [rows, setRows] = useState([]);
    const [commission, setCommission] = useState(10);
    const [copied, setCopied] = useState(false);
    const [sort, setSort] = useState({ key: 'signedUp', dir: 'desc' });
    const [page, setPage] = useState(1);

    const fetchData = useCallback(async () => {
        setError(null);
        try {
            const [referrerRes, detailRes, walletRes, commissionValue] = await Promise.all([
                supabase.from('users')
                    .select('id, full_name, email, referral_code, created_at, account_status, referred_by')
                    .eq('id', userId).maybeSingle(),
                getReferralDetail(userId),
                supabase.from('user_wallets').select('balance_usd, wallet_address').eq('user_id', userId).maybeSingle(),
                getReferralCommission(),
            ]);

            if (!referrerRes.data) {
                setNotFound(true);
                return;
            }
            setReferrer(referrerRes.data);
            setWallet(walletRes.data || null);
            setCommission(commissionValue);

            if (detailRes.success) {
                setRows(detailRes.data || []);
            } else {
                setError(detailRes.error || 'Could not load referral detail.');
            }
        } catch (err) {
            console.error('Error loading referral detail:', err);
            setError(err.message || 'Could not load referral detail.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [userId]);

    useEffect(() => {
        // ProtectedRoute checks auth and device but never the role — every admin
        // screen guards role_id === 4 itself.
        const userRole = currentAdmin?.user_metadata?.role || localStorage.getItem('role');
        if (String(userRole) !== '4') { navigate('/welcome'); return; }
        fetchData();
    }, [currentAdmin, navigate, fetchData]);

    // Totals come from the same rows the table renders, so the cards and the
    // table cannot drift apart.
    const totals = useMemo(() => {
        const verified = rows.filter((r) => r.is_verified).length;
        const credited = rows.filter(isCredited);
        return {
            referrals: rows.length,
            verified,
            unverified: rows.length - verified,
            creditedUsd: credited.reduce((s, r) => s + num(r.bonus_amount_usd), 0),
            creditedCount: credited.length,
        };
    }, [rows]);

    const pendingCount = totals.referrals - totals.creditedCount;
    const pendingUsd = pendingCount * num(commission);

    const visible = useMemo(() => {
        const accessor = SORT_ACCESSORS[sort.key] || SORT_ACCESSORS.signedUp;
        const factor = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = accessor(a);
            const bv = accessor(b);
            if (av < bv) return -1 * factor;
            if (av > bv) return 1 * factor;
            // Stable tiebreak so equal rows never jitter between renders.
            return time(b.signed_up_at) - time(a.signed_up_at);
        });
    }, [rows, sort]);

    useEffect(() => { setPage(1); }, [sort]);

    const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const toggleSort = (key) => {
        setSort((prev) => (prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: TEXT_COLUMNS.includes(key) ? 'asc' : 'desc' }));
    };

    const handleRefresh = () => { setRefreshing(true); fetchData(); };

    const handleCopyCode = () => {
        if (!referrer?.referral_code) return;
        navigator.clipboard?.writeText(referrer.referral_code)
            .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
            .catch(() => { /* clipboard unavailable */ });
    };

    const handleExportCsv = () => {
        const headers = ['Referred User', 'Email', 'Signed Up', 'Verification', 'Verified At',
            'Status', 'Bonus USD', 'Credited On', 'Transaction ID'];
        const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const body = visible.map((r) => [
            r.full_name || '',
            r.email || '',
            r.signed_up_at || '',
            r.is_verified ? 'Verified' : 'Unverified',
            r.verified_at || '',
            isCredited(r) ? 'Credited' : 'Pending',
            isCredited(r) ? num(r.bonus_amount_usd).toFixed(2) : '',
            r.bonus_confirmed_at || '',
            r.bonus_tx_id || '',
        ].map(cell).join(','));
        const csv = [headers.map(cell).join(','), ...body].join('\r\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `referrals-${referrer?.referral_code || userId}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const StatCard = ({ title, value, sublabel, icon, colorClass }) => (
        <div className="stat-card">
            <div className={`stat-icon ${colorClass}`}><i className={`bx ${icon}`}></i></div>
            <div className="stat-content">
                <div className="stat-label">{title}</div>
                <div className="stat-value">{value}</div>
                {sublabel && <div className="stat-change">{sublabel}</div>}
            </div>
        </div>
    );

    const SortableTh = ({ label, sortKey, className = '', title }) => {
        const active = sort.key === sortKey;
        const icon = active ? (sort.dir === 'desc' ? 'bx-chevron-down' : 'bx-chevron-up') : 'bx-sort-alt-2';
        return (
            <th
                className={`sortable ${className}${active ? ' sorted' : ''}`}
                onClick={() => toggleSort(sortKey)}
                title={title || `Sort by ${label}`}
                aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
            >
                {label}
                <i className={`bx sort-icon ${icon}`}></i>
            </th>
        );
    };

    if (loading) {
        return <AdminLayout><Loader /></AdminLayout>;
    }

    if (notFound) {
        return (
            <AdminLayout>
                <div className="admin-card">
                    <div className="admin-empty">
                        <i className="bx bx-error-circle"></i>
                        <p>No user found for this ID.</p>
                        <button className="btn btn-secondary" onClick={() => navigate('/admin/referrals')}>
                            <i className="bx bx-arrow-back"></i>
                            Back to Referrals
                        </button>
                    </div>
                </div>
            </AdminLayout>
        );
    }

    const displayName = referrer?.full_name || referrer?.email || 'Unknown user';

    return (
        <AdminLayout>
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <i className="bx bx-gift"></i>
                        Referral Details
                    </h1>
                    <p className="page-subtitle">Every referral made by {displayName}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button className="btn btn-secondary" onClick={handleExportCsv} disabled={visible.length === 0}
                            title="Download these referrals as CSV">
                        <i className="bx bx-download"></i>
                        Export CSV
                    </button>
                    <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshing}>
                        <i className={`bx bx-refresh ${refreshing ? 'bx-spin' : ''}`}></i>
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => navigate('/admin/referrals')}>
                        <i className="bx bx-arrow-back"></i>
                        Back to Referrals
                    </button>
                </div>
            </div>

            {error && (
                <div className="admin-alert admin-alert-danger">
                    <span className="admin-alert-message">
                        <i className="bx bx-error-circle"></i>
                        Could not load referral detail: {error}
                    </span>
                    <button className="btn btn-sm btn-secondary" onClick={handleRefresh}>Retry</button>
                </div>
            )}

            {/* Referrer identity */}
            <div className="admin-card">
                <div className="card-header card-header-wrap">
                    <h3 className="card-title">
                        <i className="bx bx-user"></i>
                        Referrer
                    </h3>
                    <div className="card-toolbar">
                        <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/admin/users/${userId}`)}>
                            <i className="bx bx-user-circle"></i>
                            View user profile
                        </button>
                    </div>
                </div>
                <div className="user-cell">
                    <div className="user-avatar-small">{displayName.charAt(0).toUpperCase()}</div>
                    <div>
                        <div className="user-cell-name" style={{ fontWeight: 600 }}>{displayName}</div>
                        <div className="user-cell-sub">{referrer?.email}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span className="user-cell-sub">Referral code</span>
                        <code className="code-chip">{referrer?.referral_code || '—'}</code>
                        {referrer?.referral_code && (
                            <button className="link-button" onClick={handleCopyCode} title="Copy referral code">
                                <i className={`bx ${copied ? 'bx-check' : 'bx-copy'}`}></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className="stats-grid stats-grid-compact">
                <StatCard
                    title="Total Referrals"
                    value={totals.referrals}
                    sublabel={rows.length ? `last ${getTimeAgo(visible[0]?.signed_up_at)}` : 'no signups yet'}
                    icon="bx-user-plus"
                    colorClass="primary"
                />
                <StatCard
                    title="Verified"
                    value={totals.verified}
                    sublabel={totals.referrals
                        ? `${Math.round((totals.verified / totals.referrals) * 100)}% of signups`
                        : '—'}
                    icon="bx-check-shield"
                    colorClass="success"
                />
                <StatCard
                    title="Unverified"
                    value={totals.unverified}
                    sublabel={totals.unverified ? 'bonus not yet due' : 'all verified'}
                    icon="bx-time-five"
                    colorClass="warning"
                />
                <StatCard
                    title="Total Credited"
                    value={formatUSD(totals.creditedUsd)}
                    sublabel={`${totals.creditedCount} bonus${totals.creditedCount === 1 ? '' : 'es'} paid`}
                    icon="bx-dollar-circle"
                    colorClass="success"
                />
                <StatCard
                    title="Pending Bonus"
                    value={formatUSD(pendingUsd)}
                    sublabel={pendingCount
                        ? `if all ${pendingCount} verify at ${formatUSD(commission)}`
                        : 'nothing outstanding'}
                    icon="bx-hourglass"
                    colorClass="info"
                />
                <StatCard
                    title="Wallet Balance"
                    value={formatUSD(wallet?.balance_usd)}
                    sublabel="spendable, all sources"
                    icon="bx-wallet"
                    colorClass="primary"
                />
            </div>

            {/* Referrals */}
            <div className="admin-card">
                <div className="card-header card-header-wrap">
                    <h3 className="card-title">
                        <i className="bx bx-list-ul"></i>
                        Referrals ({visible.length})
                    </h3>
                </div>

                {visible.length === 0 ? (
                    <div className="admin-empty">
                        <i className="bx bx-user-plus"></i>
                        <p>{error ? 'Referral data unavailable.' : 'This user has not referred anyone yet'}</p>
                    </div>
                ) : (
                    <>
                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <SortableTh label="Referred User" sortKey="name" />
                                        <SortableTh label="Signed Up" sortKey="signedUp" />
                                        <SortableTh label="Verification" sortKey="verified"
                                                    title="Whether the referred user confirmed their email address" />
                                        <SortableTh label="Status" sortKey="status"
                                                    title="Credited once the referred user verifies their email" />
                                        <SortableTh label="Bonus" sortKey="bonus" className="num" />
                                        <SortableTh label="Credited On" sortKey="creditedOn" />
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((r) => {
                                        const credited = isCredited(r);
                                        const name = r.full_name || r.email || 'N/A';
                                        return (
                                            <tr key={r.referred_user_id}>
                                                <td>
                                                    <div className="user-cell">
                                                        <div className="user-avatar-small">
                                                            {name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="user-cell-text">
                                                            <button
                                                                className="link-button user-cell-name"
                                                                onClick={() => navigate(`/admin/users/${r.referred_user_id}`)}
                                                                title={r.full_name || undefined}
                                                            >
                                                                {r.full_name || 'N/A'}
                                                            </button>
                                                            <div className="user-cell-sub" title={r.email}>{r.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td title={fullDate(r.signed_up_at)}>{smartDate(r.signed_up_at)}</td>
                                                <td>
                                                    {r.is_verified ? (
                                                        <span className="badge badge-success"
                                                              title={`Verified ${fullDate(r.verified_at)}`}>
                                                            Verified
                                                        </span>
                                                    ) : (
                                                        <span className="badge badge-warning"
                                                              title="Has not confirmed their email address yet">
                                                            Unverified
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {credited ? (
                                                        <span className="badge badge-success"
                                                              title="Bonus credited to the referrer's wallet">
                                                            Credited
                                                        </span>
                                                    ) : (
                                                        <span className="badge badge-warning"
                                                              title="Bonus is paid once this user verifies their email">
                                                            Pending
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="num">
                                                    {credited ? formatUSD(r.bonus_amount_usd) : '—'}
                                                </td>
                                                <td title={fullDate(r.bonus_confirmed_at)}>
                                                    {credited ? smartDate(r.bonus_confirmed_at) : '—'}
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() => navigate(`/admin/users/${r.referred_user_id}`)}
                                                        title="View this user's profile"
                                                    >
                                                        <i className="bx bx-show"></i>
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="admin-pagination">
                                <span className="pagination-info">
                                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                                    {Math.min(currentPage * PAGE_SIZE, visible.length)} of {visible.length} referrals
                                </span>
                                <div className="pagination-controls">
                                    <button className="btn btn-sm btn-secondary"
                                            onClick={() => setPage(currentPage - 1)} disabled={currentPage <= 1}>
                                        <i className="bx bx-chevron-left"></i>
                                        Prev
                                    </button>
                                    <span className="pagination-page">Page {currentPage} of {totalPages}</span>
                                    <button className="btn btn-sm btn-secondary"
                                            onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages}>
                                        Next
                                        <i className="bx bx-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </AdminLayout>
    );
}

export default AdminReferralDetail;
